import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import type { SolanaNetworkConfig } from "../../config/solana";
import { ethers } from "ethers";
import {
  buildAccountLoginClaimFromSpendingKeys,
  challengeMessageWithViewingKey,
  computeViewingKeyDigest,
} from "../../joinsplit/viewingKeyClaim";
import { signSolanaMessage } from "../../joinsplit-v2/solana";
import {
  readStoredProxyAuthToken,
  revokeProxyAuthSession,
  storeProxyAuthToken,
  tryRefreshProxyAuthToken,
} from "../../utils/proxy-auth";
import type { SolanaWalletState } from "../types";

export interface SolanaProxyAuthRequestOptions {
  interactive?: boolean;
  expectedRecipientKey?: string;
}

function normalizeRecipientKey(recipientKey?: string | null): string {
  return String(recipientKey || "")
    .trim()
    .toLowerCase()
    .replace(/^0x/, "");
}

function readTokenRecipientKey(token: string): string {
  try {
    const payload = JSON.parse(atob(token.split(".")[1] || "")) as {
      recipientKey?: string;
    };
    return normalizeRecipientKey(payload.recipientKey);
  } catch {
    return "";
  }
}

const PROTECTED_PREFIXES = [
  "/api/v1/notes",
  "/api/v1/history",
  "/api/v1/commitments",
  "/api/v1/recovery",
];

/**
 * Session tokens for the engine proxy, issued to the Solana wallet after it
 * signs the challenge and the account proves its Baby Jubjub key. The token
 * is scoped per network and pool so a redeploy never reuses a session.
 */
export function useSolanaProxyAuth({
  network,
  wallet,
  derivationSignatureRef,
}: {
  network: SolanaNetworkConfig;
  wallet: SolanaWalletState;
  derivationSignatureRef: MutableRefObject<Uint8Array | null>;
}) {
  const address = wallet.address;
  const connection = wallet.connection;
  const scope = useMemo(
    () => `${network.key}:${network.programId}`.toLowerCase(),
    [network.key, network.programId],
  );
  const tokenKey = `stealthos_proxy_auth_token:${network.key}:${address || "unknown"}:${scope}`;
  const tokenExpKey = `stealthos_proxy_auth_token_exp:${network.key}:${address || "unknown"}:${scope}`;
  const activeRecipientKey = useMemo(
    () =>
      wallet.spendingKeys
        ? ethers
            .sha256(
              ethers.toUtf8Bytes(ethers.toBeHex(wallet.spendingKeys.mpk, 32)),
            )
            .slice(2)
        : "",
    [wallet.spendingKeys],
  );
  const identity = `${address}:${scope}:${activeRecipientKey}`;
  const currentIdentityRef = useRef({ identity, generation: 0 });
  if (currentIdentityRef.current.identity !== identity) {
    currentIdentityRef.current = {
      identity,
      generation: currentIdentityRef.current.generation + 1,
    };
  }
  const generation = currentIdentityRef.current.generation;
  const inFlightGenerationRef = useRef(-1);
  const previousIdentityRef = useRef(identity);
  const inFlightRef = useRef<Promise<string> | null>(null);
  const [proxyAuthReady, setProxyAuthReady] = useState(false);
  const [proxyAuthPending, setProxyAuthPending] = useState(false);
  const [proxyAuthError, setProxyAuthError] = useState<string | null>(null);

  const refreshProxyAuthState = useCallback(() => {
    const token = readStoredProxyAuthToken(tokenKey, tokenExpKey);
    const ready =
      Boolean(address) &&
      Boolean(activeRecipientKey) &&
      Boolean(token) &&
      readTokenRecipientKey(token) === activeRecipientKey;
    setProxyAuthReady(ready);
    if (ready) setProxyAuthError(null);
    return ready;
  }, [address, activeRecipientKey, tokenExpKey, tokenKey]);

  const clearStoredAuthArtifacts = useCallback(() => {
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(tokenKey);
      window.sessionStorage.removeItem(tokenExpKey);
    }
    setProxyAuthReady(false);
    setProxyAuthError(null);
  }, [tokenExpKey, tokenKey]);

  useEffect(() => {
    if (previousIdentityRef.current === identity) return;
    previousIdentityRef.current = identity;
    const token = readStoredProxyAuthToken(tokenKey, tokenExpKey);
    if (
      !activeRecipientKey ||
      readTokenRecipientKey(token) !== activeRecipientKey
    )
      clearStoredAuthArtifacts();
    else refreshProxyAuthState();
    setProxyAuthPending(false);
  }, [
    identity,
    activeRecipientKey,
    tokenKey,
    tokenExpKey,
    clearStoredAuthArtifacts,
    refreshProxyAuthState,
  ]);

  useEffect(() => {
    refreshProxyAuthState();
    if (typeof window === "undefined") return;
    const interval = window.setInterval(refreshProxyAuthState, 15_000);
    return () => window.clearInterval(interval);
  }, [refreshProxyAuthState]);

  const ensureProxyAuthToken = useCallback(
    async (options?: SolanaProxyAuthRequestOptions): Promise<string> => {
      const assertCurrent = () => {
        if (currentIdentityRef.current.generation !== generation)
          throw new Error(
            "Private account changed during sign-in; retry with the current wallet",
          );
      };
      assertCurrent();
      if (!address || !connection) {
        setProxyAuthReady(false);
        throw new Error("Connect a Solana wallet before using private actions");
      }
      if (!wallet.spendingKeys || !activeRecipientKey)
        throw new Error("Unlock the private wallet before signing in");
      const requestedRecipientKey = normalizeRecipientKey(
        options?.expectedRecipientKey,
      );
      if (requestedRecipientKey && requestedRecipientKey !== activeRecipientKey)
        throw new Error(
          "Requested private account does not match the unlocked wallet",
        );
      const expectedRecipientKey = activeRecipientKey;
      const acceptToken = (token: string) => {
        assertCurrent();
        const tokenRecipientKey = readTokenRecipientKey(token);
        if (tokenRecipientKey !== expectedRecipientKey) {
          clearStoredAuthArtifacts();
          throw new Error(
            "Proxy auth session is for a different private account; sign in again",
          );
        }
        setProxyAuthReady(true);
        setProxyAuthError(null);
        return token;
      };
      if (inFlightRef.current && inFlightGenerationRef.current === generation)
        return acceptToken(await inFlightRef.current);
      const existing = readStoredProxyAuthToken(tokenKey, tokenExpKey);
      if (existing) {
        try {
          return acceptToken(existing);
        } catch (error) {
          if (options?.interactive !== true) throw error;
        }
      }
      const inFlight = (async () => {
        if (options?.interactive === true) {
          setProxyAuthPending(true);
          setProxyAuthError(null);
        }
        const refreshed = await tryRefreshProxyAuthToken(address);
        assertCurrent();
        if (refreshed?.token) {
          const refreshedRecipientKey = readTokenRecipientKey(refreshed.token);
          if (refreshedRecipientKey === expectedRecipientKey) {
            storeProxyAuthToken(
              tokenKey,
              tokenExpKey,
              refreshed.token,
              refreshed.expiresAt,
            );
            return refreshed.token;
          }
          clearStoredAuthArtifacts();
        }
        if (options?.interactive !== true) {
          setProxyAuthReady(false);
          throw new Error("Proxy auth token unavailable");
        }
        if (!wallet.spendingKeys) {
          throw new Error("Unlock the private wallet before signing in");
        }
        const nonceResponse = await fetch("/api/auth/nonce", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ wallet: address }),
        });
        if (!nonceResponse.ok) {
          throw new Error(
            `Proxy auth nonce failed: ${(await nonceResponse.text()) || nonceResponse.status}`,
          );
        }
        const nonceData = (await nonceResponse.json()) as {
          message: string;
          nonce: string;
          challenge: string;
          walletKind?: string;
        };
        assertCurrent();
        if (nonceData.walletKind !== "solana") {
          throw new Error("Proxy auth did not recognise the Solana wallet");
        }
        // Recovery may load keys independently of the latest setup signature.
        // Authenticate those active keys; only the public claim and per-nonce
        // ownership proof are submitted.
        const { claim: viewingKey, proof: accountProof } =
          await buildAccountLoginClaimFromSpendingKeys(
            wallet.spendingKeys,
            address,
            String(nonceData.nonce || ""),
          );
        const digest = await computeViewingKeyDigest(viewingKey);
        assertCurrent();
        const signature = await signSolanaMessage(
          connection,
          new TextEncoder().encode(
            challengeMessageWithViewingKey(nonceData.message, digest),
          ),
        );
        assertCurrent();
        const verifyResponse = await fetch("/api/auth/verify", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            wallet: address,
            challenge: nonceData.challenge,
            signature: btoa(String.fromCharCode(...signature)),
            viewingKey,
            accountProof,
          }),
        });
        if (!verifyResponse.ok) {
          throw new Error(
            `Proxy auth verify failed: ${(await verifyResponse.text()) || verifyResponse.status}`,
          );
        }
        const verifyData = (await verifyResponse.json()) as {
          token: string;
          expiresAt?: number | null;
          recipientKey?: string;
        };
        assertCurrent();
        if (!verifyData.token)
          throw new Error("Proxy auth verify returned no token");
        const verifyRecipientKey = readTokenRecipientKey(verifyData.token);
        if (verifyRecipientKey !== expectedRecipientKey) {
          clearStoredAuthArtifacts();
          revokeProxyAuthSession(address);
          throw new Error(
            "Proxy auth recipient did not match the connected private account; reconnect the wallet",
          );
        }
        storeProxyAuthToken(
          tokenKey,
          tokenExpKey,
          verifyData.token,
          verifyData.expiresAt,
        );
        return verifyData.token;
      })();
      inFlightGenerationRef.current = generation;
      inFlightRef.current = inFlight;
      try {
        return acceptToken(await inFlight);
      } catch (error) {
        assertCurrent();
        setProxyAuthReady(false);
        if (options?.interactive === true) {
          setProxyAuthError(
            error instanceof Error ? error.message : String(error),
          );
        }
        throw error;
      } finally {
        if (inFlightRef.current === inFlight) inFlightRef.current = null;
        if (
          currentIdentityRef.current.generation === generation &&
          options?.interactive === true
        )
          setProxyAuthPending(false);
      }
    },
    [
      address,
      connection,
      wallet.spendingKeys,
      activeRecipientKey,
      generation,
      clearStoredAuthArtifacts,
      derivationSignatureRef,
      tokenExpKey,
      tokenKey,
    ],
  );

  const authHeadersForPath = useCallback(
    async (
      path: string,
      options?: SolanaProxyAuthRequestOptions,
    ): Promise<Record<string, string>> => {
      const pathOnly = path.split("?")[0];
      if (!PROTECTED_PREFIXES.some((prefix) => pathOnly.startsWith(prefix)))
        return {};
      const requiresAuth =
        pathOnly === "/api/v1/history/store" ||
        pathOnly === "/api/v1/notes/spent" ||
        pathOnly === "/api/v1/notes/store" ||
        pathOnly.startsWith("/api/v1/recovery/journal/");
      try {
        const token = await ensureProxyAuthToken(options);
        return { Authorization: `Bearer ${token}` };
      } catch (error) {
        if (options?.interactive === true || requiresAuth) throw error;
        return {};
      }
    },
    [ensureProxyAuthToken],
  );

  const clearProxyAuthToken = useCallback(() => {
    clearStoredAuthArtifacts();
    inFlightRef.current = null;
    if (address) revokeProxyAuthSession(address);
  }, [address, clearStoredAuthArtifacts]);

  return {
    ensureProxyAuthToken,
    authHeadersForPath,
    clearProxyAuthToken,
    proxyAuthReady,
    proxyAuthPending,
    proxyAuthError,
    refreshProxyAuthState,
  };
}
